import { ChangeDetectionStrategy, Component, Directive, computed, contentChild, input } from '@angular/core';

import { LabelListLayoutEnum, type LabelListLayout } from './label-list.types';

/**
 * Marks the element that replaces a row's plain text value: `<span ddValue>`, or the
 * `<ng-container ddValue>` that hosts a read display (#21239). Nothing but a projection handle —
 * `dd-label-list` queries it to know a rich value took over.
 *
 * A consumer must IMPORT this directive alongside `LabelListComponent`: `contentChild` matches on
 * the directive instance, not on the attribute, so a bare `ddValue` attribute in a template that
 * does not import it projects fine and is never detected — the row would then print both the
 * projected value and the plain one.
 */
@Directive({ selector: '[ddValue]' })
export class DdValueDirective {}

/**
 * Label/value pair row for read-only detail views — Figma "Label list" (master 1413:36125,
 * onboarding-review contract §4). Two-column grid item by default; `fullWidth` spans the full
 * grid row (long values) and `multiline` disables the single-line truncate (wrapped values).
 *
 * The value is either the `value` string or, for callers that need something richer than text
 * (a badge, a chip list, a read display resolved from the registry), projected content marked
 * `ddValue`. Projection lands INSIDE the value span, so a projected value inherits the row's
 * type, color, truncate and sentry-mask instead of restating them.
 *
 * Presentational only: data comes in via inputs, nothing is fetched here. Colors come from the
 * `--dd-accent` (label) and `--dd-card-border` (divider) design tokens plus the `text-default`
 * and `text-hint` Fuse utilities — see `src/styles/components/_forms.scss` /
 * `_status-badges.scss`. Never hardcode these so dark mode keeps working.
 */
@Component({
  selector: 'dd-label-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'border-b border-[color:var(--dd-card-border)] min-w-0',
    '[class]': 'layoutClasses()',
    // col-span-full, not col-span-2: below the sm breakpoint the parent grid is grid-cols-1,
    // where span 2 forces an implicit second track and narrows every sibling row by the
    // column gap. Full-row span is identical on the 2-column desktop grid.
    '[class.col-span-full]': 'fullWidth()',
  },
  template: `
    <span [class]="labelClasses()">{{ label() }}</span>
    <!-- sentry-mask on the VALUE only: this is where entry data lands (names, addresses, e-mail
         addresses), while the label above it is interface text that a replay needs to stay
         readable. Masking here is what makes blanket maskAllText avoidable outside production. -->
    <!-- overflow-clip, not Tailwind's 'truncate' (overflow:hidden): a projected [ddValue] can be
         a focusable link (dd-link-display's mailto/tel/url), and its focus box-shadow sits just
         outside the link's own box — overflow:hidden clips that ring along with the overflow.
         overflow:clip paints nothing beyond the box EXCEPT the margin overflow-clip-margin
         declares, so the ring stays visible while long text still ellipsizes (#21239 follow-up,
         task 21245). text-ellipsis + whitespace-nowrap are the other two thirds of 'truncate',
         kept as-is. In a browser without overflow-clip-margin support the declaration is dropped
         and overflow:clip falls back to a zero margin — long text still ellipsizes, only the
         focus ring is clipped again, which is exactly today's behaviour elsewhere and never a
         rendering break. -->
    <span
      class="min-w-0 text-[0.9375rem] font-medium leading-[1.4] text-default sentry-mask"
      [class.overflow-clip]="!multiline()"
      [class.text-ellipsis]="!multiline()"
      [class.whitespace-nowrap]="!multiline()"
      [style.overflow-clip-margin]="multiline() ? null : '4px'"
      [attr.data-cy]="dataCy()">
      <ng-content select="[ddValue]" />
      @if (!hasCustomValue()) {
        {{ value() ?? '—' }}
      }
    </span>
    <!-- The hint belongs INSIDE the row, above the divider: a hint under the border reads as
         belonging to the next row. Same tokens as a field component's mat-hint. -->
    @if (hint(); as hintText) {
      <!-- col-start-2 lines the hint up under the value in the inline grid; a no-op when stacked. -->
      <span class="col-start-2 text-[0.75rem] leading-[1.4] text-hint">{{ hintText }}</span>
    }
  `,
})
export class LabelListComponent {
  readonly label = input.required<string>();
  readonly value = input<string | null>(null);
  /** Explanatory line under the value (a field description), still above the row's divider. */
  readonly hint = input<string | null>(null);
  /** Disables the single-line truncate — long values wrap over multiple lines instead. */
  readonly multiline = input(false);
  /** Spans the parent grid's full row (both columns on the 2-column desktop grid). */
  readonly fullWidth = input(false);
  /**
   * `stacked` (default) is Figma's "Label list": small accent label ABOVE the value — the
   * onboarding-review and document-card contract. `inline` puts a muted label LEFT of the value
   * in a fixed 3:5 column split, the compact scan-first row the detail read view uses (#21239
   * feedback round 2026-09-15): it saves a text line per field and lines every value up in one
   * column, so a card reads like a spec sheet.
   */
  readonly layout = input<LabelListLayout>(LabelListLayoutEnum.Stacked);
  /** Optional data-cy on the value element, for callers that need a stable test hook. */
  readonly dataCy = input<string | null>(null);

  // NOT `#customValue`: Angular rejects a signal query on an ES-private member (NG1053), so this
  // one field deviates from the project's `readonly #field` convention.
  protected readonly customValue = contentChild(DdValueDirective);

  /**
   * Projected content WINS over `value`: the read row (`readonly-field-row.component.ts`, the
   * only projecting caller today) hands over a display component and passes no `value` at all, so
   * without this the row would print the display AND the `—` fallback below it. Callers that pass
   * both — a badge plus its text, say — get the projection and nothing else, for the same reason:
   * printing both would read the answer twice.
   */
  readonly hasCustomValue = computed(() => this.customValue() !== undefined);

  protected readonly layoutClasses = computed(() =>
    this.layout() === LabelListLayoutEnum.Inline
      ? 'grid grid-cols-[minmax(0,3fr)_minmax(0,5fr)] items-center gap-x-4 gap-y-1 py-3'
      : 'flex flex-col gap-2 pb-3'
  );

  protected readonly labelClasses = computed(() =>
    this.layout() === LabelListLayoutEnum.Inline
      ? 'text-[0.8125rem] font-normal leading-[1.4] text-secondary min-w-0'
      : 'text-[0.75rem] font-medium leading-none text-[color:var(--dd-accent)]'
  );
}
